import { useMemo } from "react";
import { formatTimeRu, toDateInputValue } from "../../../utils/appointmentLabels";
import { IconBell } from "../../ui/icons";
import {
  CALENDAR_GRID_HEIGHT_PX,
  CALENDAR_HOURS,
  sessionColor,
  sessionLabel,
} from "./calendarUtils";
import {
  eventBlockStyle,
  isWeekendDay,
  layoutDayEvents,
} from "./desktopCalendarLayout";
import md from "../common/manageDesktop.module.css";

const GRID_HEIGHT = CALENDAR_GRID_HEIGHT_PX;

const WEEKDAY_SHORT = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];

function daySessions(sessions, day) {
  return sessions
    .filter((sess) => sess.start_datetime?.slice(0, 10) === day)
    .sort(
      (a, b) =>
        new Date(a.start_datetime) - new Date(b.start_datetime),
    );
}

function DesktopEventBlock({ item, groups, onOpenSession }) {
  const groupIdx = groups.findIndex((g) => g.id === item.sess.group?.id);
  const color = sessionColor(item.sess, groupIdx);

  return (
    <button
      type="button"
      className={md.desktopDayEvent}
      style={{
        ...eventBlockStyle(item, { hourStart: 0 }),
        "--event-color": color,
      }}
      onClick={() => onOpenSession?.(item.sess)}
    >
      <span className={md.desktopDayEventInner}>
        <span className={md.desktopDayEventTitleRow}>
          <IconBell
            width={14}
            height={14}
            className={md.desktopDayEventIcon}
          />
          <span className={md.desktopDayEventTitle}>
            {sessionLabel(item.sess)}
          </span>
        </span>
        <span className={md.desktopDayEventTime}>
          {formatTimeRu(item.sess.start_datetime)}
        </span>
      </span>
    </button>
  );
}

export default function DesktopWeekTimeGridView({
  days,
  sessions,
  groups,
  onOpenSession,
}) {
  const todayStr = toDateInputValue();

  const layoutsByDay = useMemo(() => {
    const map = {};
    for (const day of days) {
      map[day] = layoutDayEvents(daySessions(sessions, day));
    }
    return map;
  }, [days, sessions]);

  return (
    <div
      className={md.desktopWeekGridWrap}
      style={{ "--week-cols": days.length }}
    >
      <div className={md.desktopWeekGridScroll}>
        <div className={md.desktopWeekHeaderRow}>
          <div className={md.desktopWeekCorner} />
          {days.map((day) => {
            const d = new Date(`${day}T12:00:00`);
            const isToday = day === todayStr;
            const weekend = isWeekendDay(day);
            return (
              <div
                key={`h-${day}`}
                className={[
                  md.desktopWeekDayHead,
                  isToday ? md.desktopWeekDayHeadToday : "",
                  weekend ? md.desktopWeekDayHeadWeekend : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <span className={md.desktopWeekDayName}>
                  {WEEKDAY_SHORT[d.getDay()]}
                </span>
                <span className={md.desktopWeekDayDate}>
                  {d.getDate()}
                </span>
              </div>
            );
          })}
        </div>

        <div className={md.desktopWeekBody}>
          <div
            className={md.desktopWeekTimeCol}
            style={{ height: `${GRID_HEIGHT}px` }}
          >
            {CALENDAR_HOURS.map((hour) => (
              <div key={hour} className={md.desktopDayTimeLabel}>
                {String(hour).padStart(2, "0")}:00
              </div>
            ))}
            <div className={md.desktopDayTimeEnd}>24:00</div>
          </div>

          {days.map((day) => {
            const weekend = isWeekendDay(day);
            return (
              <div
                key={day}
                className={[
                  md.desktopWeekDayCol,
                  weekend ? md.desktopWeekDayColWeekend : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                style={{ height: `${GRID_HEIGHT}px` }}
              >
                <div className={md.desktopDayHourLines} aria-hidden>
                  {CALENDAR_HOURS.map((hour) => (
                    <div key={hour} className={md.desktopDayHourLine} />
                  ))}
                </div>
                <div className={md.desktopDayEventsLayer}>
                  {(layoutsByDay[day] || []).map((item) => (
                    <DesktopEventBlock
                      key={item.sess.id}
                      item={item}
                      groups={groups}
                      onOpenSession={onOpenSession}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
